// Basit, global bir unread-count store + SSE entegrasyonu
import { useEffect, useSyncExternalStore } from "react";
import { realtimeBus } from "../realtime/RealtimeBus";
import { fetchNotifications } from "../lib/api";

type State = { unread: number | null }; // null = bilinmiyor (ilk yükleme)
let state: State = { unread: null };

const listeners = new Set<() => void>();
function emit() {
  listeners.forEach((l) => {
    try { l(); } catch {}
  });
}

export function getUnreadCount() {
  return state.unread ?? 0;
}
export function setUnreadCount(v: number) {
  state = { unread: Math.max(0, v | 0) };
  emit();
}
export function incUnread(delta: number) {
  setUnreadCount(Math.max(0, getUnreadCount() + delta));
}

// ────────────────────────────────────────────────────────────
// İlk yükleme + dışarıdan manuel tazeleme
// ────────────────────────────────────────────────────────────
let inited = false;

export async function forceRefreshUnreadCount() {
  try {
    const r = await fetch("/api/notifications/count", { credentials: "include" });
    if (r.ok) {
      const data = (await r.json().catch(() => null)) as any;
      if (data && typeof data.count === "number") {
        setUnreadCount(data.count);
        return;
      }
    }
    // Fallback: ilk sayfadan yaklaşık tahmin (sadece unread)
    const list = await fetchNotifications({ limit: 10, unread: true }).catch(() => null);
    if (list?.ok) setUnreadCount(list.items.length);
  } catch {}
}

async function ensureInitOnce() {
  if (inited) return;
  inited = true;
  await forceRefreshUnreadCount();
}

// ────────────────────────────────────────────────────────────
// Hook
// ────────────────────────────────────────────────────────────
export function useUnreadNotifications() {
  useEffect(() => {
    ensureInitOnce();

    // SSE ile canlı güncelle
    const off = realtimeBus.onNotification(({ payload }) => {
      try {
        // Backend: { kind: "unread_count", count }
        if (payload && payload.kind === "unread_count" && typeof payload.count === "number") {
          setUnreadCount(payload.count);
          return;
        }
        // Koruyucu hızlı yol: hepsi okundu sinyali
        if (payload && payload.kind === "read_all") {
          setUnreadCount(0);
          return;
        }
        // Not: "new", "read" gibi sinyalleri burada sayaca çevirmiyoruz.
        // Kaynak gerçeklik backend → unread_count olduğundan drift riski almıyoruz.
      } catch {}
    });

    return () => { off(); };
  }, []);

  // React 18 concurrent uyumlu subscribe
  const subscribe = (cb: () => void) => { listeners.add(cb); return () => listeners.delete(cb); };
  const getSnapshot = () => getUnreadCount();
  const getServerSnapshot = () => 0;

  const unread = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return unread;
}

// ────────────────────────────────────────────────────────────
// İyimser güncelleme yardımcıları
// ────────────────────────────────────────────────────────────
export function optimisticMarkOneRead() {
  setUnreadCount(Math.max(0, getUnreadCount() - 1));
}
export function optimisticMarkAllRead() {
  setUnreadCount(0);
}
